import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  TableContainer,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  IconButton,
  Tooltip,
  CircularProgress,
  Backdrop
} from '@mui/material';
import { Autocomplete } from '@mui/material';
import { getDepartmentsList } from 'Department/Domain/Services/GetDepartmentsListService';
import { getUsersList } from 'User/Domain/Services/GetUsersListService';
import { 
  CheckCircle as ApproveIcon, 
  Cancel as RejectIcon, 
  Visibility as ViewDocsIcon,
  FilterList as FilterIcon,
  ArrowBack as ArrowBackIcon,
  Person as ReassignIcon
} from '@mui/icons-material';
import { IMPROVEMENT_CHANNEL } from 'User/Application/Constants/PathsConstant';
// Servicios refactorizados
import { getImprovementProposalList } from '../../Domain/Services/GetImprovementProposalListService';
import { patchImprovementProposal } from '../../Domain/Services/PatchImprovementProposalService';
import { getDocuments } from '../../../Document/Domain/Services/GetDocumentsService';
import Loader from 'UI/Application/Components/Loaders/Loader';
import { toast } from 'react-toastify';
import { useAuthContext } from 'Shared/Security/Domain/Contexts/authContext';

export default function ImprovementProposalApprovalView() {
  const navigate = useNavigate();
  const { isAdmin, getMeFromLocalStorage } = useAuthContext();
  const [proposals, setProposals] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [openApproveDialog, setOpenApproveDialog] = React.useState(false);
  const [openRejectDialog, setOpenRejectDialog] = React.useState(false);
  const [openDocsDialog, setOpenDocsDialog] = React.useState(false);
  const [selectedProposal, setSelectedProposal] = React.useState(null);
  const [rejectionReason, setRejectionReason] = React.useState('');
  const [documents, setDocuments] = React.useState([]);
  // Reasignación
  const [openReassignDialog, setOpenReassignDialog] = React.useState(false);
  const [users, setUsers] = React.useState([]); // [{id, name}]
  const [selectedUserId, setSelectedUserId] = React.useState(null);
  // Departamentos
  const [departments, setDepartments] = React.useState([]); // [{id,name}]
  const [filterDepartments, setFilterDepartments] = React.useState([]); // array<int>
  const departmentMap = React.useMemo(() => {
    const map = new Map();
    departments.forEach(d => map.set(d.id, d.name));
    return map;
  }, [departments]);

  React.useEffect(() => {
    (async () => {
      try {
        const resp = await getDepartmentsList({ limit: 500, offset: 0 });
        const list = Array.isArray(resp?.records) ? resp.records : [];
        setDepartments(list.map(r => ({ id: r.id, name: r.name || r.description || `Dept ${r.id}` })));
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Error cargando departamentos', e);
      }
    })();
  }, []);
  const [actionLoading, setActionLoading] = React.useState(false);
  const [actionMessage, setActionMessage] = React.useState('');
  
  const canManage = (p) => {
    if (!p) return false;
    if (isAdmin && typeof isAdmin === 'function' && isAdmin()) return true;
    const me = getMeFromLocalStorage?.();
    return me?.id && p.trackingUserId === me.id;
  };
  
  // Ver documento en nueva pestaña (mismo patrón que FirstStep)
  const handleDownloadDocument = (doc) => {
    try {
      const fileURL = `/api/v1/blob-storage/${doc.path}`;
      window.open(fileURL, '_blank');
    } catch (error) {
      toast.error('No se pudo abrir el archivo');
    }
  };
  
  // Filtros
  const [filters, setFilters] = React.useState({
    id: '',
    dateFrom: '',
    dateTo: '',
    creator: '',
    assigned: '',
    description: '',
    goal: '',
    process: ''
  });

  const handleFilterChange = (field) => (e) => {
    setFilters((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const clearFilters = () => {
    setFilters({ id: '', dateFrom: '', dateTo: '', creator: '', assigned: '', description: '', goal: '', process: '' });
    setFilterDepartments([]);
  };

  const loadProposals = React.useCallback(async () => {
    try {
      const list = await getImprovementProposalList({ limit: 100, offset: 0 });
      if (list?.records) {
        setProposals(list.records.filter(p => p.status === 'pending'));
      }
    } catch (error) {
      console.error('Error loading proposals:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadProposals();
  }, [loadProposals]);

  const handleApprove = async () => {
    if (!selectedProposal) return;
    try {
      setActionMessage('Aprobando...');
      setActionLoading(true);
      await patchImprovementProposal(selectedProposal.id, { workflowAction: 'approve' });
      toast.success('Propuesta aprobada correctamente');
      setOpenApproveDialog(false);
      setSelectedProposal(null);
      await loadProposals();
    } catch (error) {
      toast.error('Error al aprobar la propuesta');
    } finally {
      setActionLoading(false);
      setActionMessage('');
    }
  };

  const handleReject = async () => {
    if (!selectedProposal) return;
    try {
      setActionMessage('Rechazando...');
      setActionLoading(true);
      await patchImprovementProposal(selectedProposal.id, { workflowAction: 'reject', rejectionReason });
      toast.success('Propuesta rechazada correctamente');
      setOpenRejectDialog(false);
      setSelectedProposal(null);
      setRejectionReason('');
      await loadProposals();
    } catch (error) {
      toast.error('Error al rechazar la propuesta');
    } finally {
      setActionLoading(false);
      setActionMessage('');
    }
  };

  const handleViewDocuments = async (proposal) => {
    try {
      setActionMessage('Cargando documentos...');
      setActionLoading(true);
      const docs = await getDocuments({ className: 'ImprovementProposal', idClassName: proposal.id });
      setDocuments(docs || []);
      setSelectedProposal(proposal);
      setOpenDocsDialog(true);
    } catch (error) {
      toast.error('Error al cargar documentos');
    } finally {
      setActionLoading(false);
      setActionMessage('');
    }
  };

  // Reasignación: abrir diálogo y cargar usuarios si es necesario
  const handleOpenReassign = async (proposal) => {
    setSelectedProposal(proposal);
    setSelectedUserId(proposal.trackingUserId || null);
    try {
      if (users.length === 0) {
        const list = await getUsersList({ limit: 1000, offset: 0 });
        const records = Array.isArray(list?.records) ? list.records : [];
        const options = records.map(u => {
          const parts = [u.name, u.firstSurname, u.secondSurname].filter(Boolean);
          const fullName = parts.join(' ').trim();
          const email = u.email || '';
          const label = fullName ? `${fullName} (${email})` : (email ? email : `Usuario ${u.id}`);
          return { id: u.id, label };
        });
        setUsers(options);
      }
      setOpenReassignDialog(true);
    } catch (e) {
      toast.error('No se pudieron cargar los usuarios');
    }
  };

  const handleConfirmReassign = async () => {
    if (!selectedProposal || !selectedUserId || selectedUserId === selectedProposal.trackingUserId) {
      setOpenReassignDialog(false);
      return;
    }
    try {
      setActionMessage('Reasignando...');
      setActionLoading(true);
      await patchImprovementProposal(selectedProposal.id, { trackingUserId: selectedUserId });
      toast.success('Propuesta reasignada correctamente');
      setOpenReassignDialog(false);
      setSelectedProposal(null);
      await loadProposals();
    } catch (error) {
      toast.error('Error al reasignar la propuesta');
    } finally {
      setActionLoading(false);
      setActionMessage('');
    }
  };

  // Opciones únicas para autocompletar (creador y asignado)
  const creatorOptions = React.useMemo(() => {
    const set = new Set(
      (proposals || [])
        .map(p => (p?.createdByUserName || '').toString().trim())
        .filter(Boolean)
    );
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [proposals]);

  const assignedOptions = React.useMemo(() => {
    const set = new Set(
      (proposals || [])
        .map(p => ((p?.trackingUserName || p?.trackingUserId || '') + '').toString().trim())
        .filter(Boolean)
    );
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [proposals]);

  const filteredProposals = React.useMemo(() => {
    return proposals.filter(p => {
      const createdAt = p.createdAt instanceof Date ? p.createdAt : new Date(p.createdAt);
      if (filters.id && !p.id.toString().includes(filters.id)) return false;
      if (filters.dateFrom) {
        const from = new Date(`${filters.dateFrom}T00:00:00`);
        if (createdAt < from) return false;
      }
      if (filters.dateTo) {
        const to = new Date(`${filters.dateTo}T23:59:59.999`);
        if (createdAt > to) return false;
      }
      if (filters.creator) {
        const creator = (p.createdByUserName || '').toLowerCase();
        if (!creator.includes(filters.creator.toLowerCase())) return false;
      }
      if (filters.assigned) {
        const assigned = (p.trackingUserName || p.trackingUserId || '').toString().toLowerCase();
        if (!assigned.includes(filters.assigned.toLowerCase())) return false;
      }
      if (filters.description && !p.currentSituationDescription?.toLowerCase().includes(filters.description.toLowerCase())) return false;
      if (filters.goal && !p.improvementGoal?.toLowerCase().includes(filters.goal.toLowerCase())) return false;
      if (filters.process) {
        const proc = (p.relatedProcess || '').toLowerCase();
        if (!proc.includes(filters.process.toLowerCase())) return false;
      }
      if (filterDepartments.length > 0) {
        const rel = Array.isArray(p.relatedDepartments) ? p.relatedDepartments : [];
        const hasAny = rel.some(id => filterDepartments.includes(id));
        if (!hasAny) return false;
      }
      return true;
    });
  }, [proposals, filters, filterDepartments]);

  const renderDepartmentChips = (ids) => {
    if (!Array.isArray(ids) || ids.length === 0) return <Typography component="span" variant="caption" color="text.secondary">-</Typography>;
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center' }}>
        {ids.map(id => {
          const name = departmentMap.get(id) || id;
          return (
            <Box
              key={id}
              component="span"
              sx={{
                px: 1.2,
                py: 0.4,
                fontSize: '0.65rem',
                lineHeight: 1.2,
                bgcolor: 'grey.200',
                color: 'grey.900',
                borderRadius: '999px',
                fontWeight: 500,
                display: 'inline-block',
                maxWidth: '140px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
              title={String(name)}
            >
              {name}
            </Box>
          );
        })}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Loader />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box display="flex" alignItems="center" mb={1}>
        <IconButton size="small" onClick={() => navigate(IMPROVEMENT_CHANNEL)}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="body2" sx={{ ml: 1, cursor: 'pointer' }} onClick={() => navigate(IMPROVEMENT_CHANNEL)}>Volver</Typography>
      </Box>
      <Typography variant="h5" mb={2}>Aceptación / Rechazo de mejoras</Typography>
      
      {/* Filtros (siempre visibles) */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" alignItems="center" mb={2}>
          <FilterIcon sx={{ mr: 1 }} />
          <Typography variant="h6">Filtros</Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} md={2}>
            <TextField fullWidth size="small" label="ID" value={filters.id} onChange={handleFilterChange('id')} />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField fullWidth size="small" type="date" label="Fecha desde" value={filters.dateFrom} onChange={handleFilterChange('dateFrom')} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField fullWidth size="small" type="date" label="Fecha hasta" value={filters.dateTo} onChange={handleFilterChange('dateTo')} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} md={3}>
            <Autocomplete
              size="small"
              options={creatorOptions}
              value={filters.creator || null}
              onChange={(e, v) => setFilters(prev => ({ ...prev, creator: v || '' }))}
              renderInput={(params) => (
                <TextField {...params} fullWidth label="Creador" />
              )}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Autocomplete
              size="small"
              options={assignedOptions}
              value={filters.assigned || null}
              onChange={(e, v) => setFilters(prev => ({ ...prev, assigned: v || '' }))}
              renderInput={(params) => (
                <TextField {...params} fullWidth label="Asignado a" />
              )}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField fullWidth size="small" label="Descripción situación" value={filters.description} onChange={handleFilterChange('description')} />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField fullWidth size="small" label="¿Qué se pretende mejorar?" value={filters.goal} onChange={handleFilterChange('goal')} />
          </Grid>
          <Grid item xs={12} md={4}>
            <Autocomplete
              multiple
              size="small"
              options={departments}
              getOptionLabel={(o) => o.name || ''}
              value={departments.filter(d => filterDepartments.includes(d.id))}
              onChange={(e, values) => setFilterDepartments(values.map(v => v.id))}
              renderInput={(params) => (
                <TextField {...params} fullWidth label="Departamentos" placeholder="Filtrar..." />
              )}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Proceso relacionado"
              value={filters.process}
              onChange={handleFilterChange('process')}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button fullWidth size="small" variant="outlined" onClick={clearFilters}>Limpiar</Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabla */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align="center">ID</TableCell>
              <TableCell align="center">Fecha</TableCell>
              <TableCell align="center">Creado por</TableCell>
              <TableCell align="center">Asignado a</TableCell>
              <TableCell align="center">Proceso relacionado</TableCell>
              <TableCell align="center">Departamentos</TableCell>
              <TableCell align="center">Descripción situación</TableCell>
              <TableCell align="center">¿Qué se pretende mejorar?</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredProposals.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">No hay propuestas pendientes</TableCell>
              </TableRow>
            )}
            {filteredProposals.map((proposal) => (
              <TableRow key={proposal.id} hover>
                <TableCell align="center">
                  <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center">
                    <Tooltip title="Ver detalle de la propuesta">
                      <Button
                        variant="text"
                        size="small"
                        sx={{ mt: 0.5, textTransform: 'none' }}
                        onClick={() => navigate(`/private/user/improvement-proposal/${proposal.id}`)}
                      >
                        {proposal.id}
                      </Button>
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell align="center">
                  {new Date(proposal.createdAt).toLocaleDateString()}
                </TableCell>
                <TableCell align="center">{proposal.createdByUserName || proposal.creatorUserId || 'N/A'}</TableCell>
                <TableCell align="center">{proposal.trackingUserName || proposal.trackingUserId || 'N/A'}</TableCell>
                <TableCell align="center">{proposal.relatedProcess || '-'}</TableCell>
                <TableCell align="center">{renderDepartmentChips(proposal.relatedDepartments)}</TableCell>
                <TableCell align="center">
                  {proposal.currentSituationDescription || '-'}
                </TableCell>
                <TableCell align="center">
                  {proposal.improvementGoal || '-'}
                </TableCell>
                <TableCell align="center">
                  {canManage(proposal) && (
                    <>
                      <Tooltip title="Aprobar">
                        <IconButton 
                          color="success" 
                          disabled={actionLoading}
                          onClick={() => {
                            setSelectedProposal(proposal);
                            setOpenApproveDialog(true);
                          }}
                        >
                          <ApproveIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Rechazar">
                        <IconButton 
                          color="error" 
                          disabled={actionLoading}
                          onClick={() => {
                            setSelectedProposal(proposal);
                            setOpenRejectDialog(true);
                          }}
                        >
                          <RejectIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Reasignar responsable">
                        <IconButton 
                          color="primary"
                          disabled={actionLoading}
                          onClick={() => handleOpenReassign(proposal)}
                        >
                          <ReassignIcon />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                  <Tooltip title="Ver documentos">
                    <IconButton 
                      color="primary" 
                      disabled={actionLoading}
                      onClick={() => handleViewDocuments(proposal)}
                    >
                      <ViewDocsIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog Aprobar */}
      <Dialog open={openApproveDialog} onClose={() => setOpenApproveDialog(false)}>
        <DialogTitle>Confirmar aprobación</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Está seguro de que desea aprobar la propuesta #{selectedProposal?.id}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenApproveDialog(false)} disabled={actionLoading}>Cancelar</Button>
          <Button onClick={handleApprove} color="success" variant="contained" disabled={actionLoading}>
            Aprobar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Reasignar */}
      <Dialog open={openReassignDialog} onClose={() => setOpenReassignDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reasignar responsable</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Propuesta #{selectedProposal?.id}
          </Typography>
          <Autocomplete
            options={users}
            getOptionLabel={(o) => o?.label || ''}
            value={users.find(u => u.id === selectedUserId) || null}
            onChange={(e, v) => setSelectedUserId(v?.id || null)}
            renderInput={(params) => (
              <TextField {...params} fullWidth label="Asignado a" placeholder="Buscar..." />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenReassignDialog(false)} disabled={actionLoading}>Cancelar</Button>
          <Button 
            onClick={handleConfirmReassign}
            variant="contained"
            disabled={actionLoading || !selectedUserId || (selectedProposal && selectedUserId === selectedProposal.trackingUserId)}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Rechazar */}
      <Dialog open={openRejectDialog} onClose={() => setOpenRejectDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Rechazar propuesta</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Propuesta #{selectedProposal?.id}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Motivo del rechazo"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRejectDialog(false)} disabled={actionLoading}>Cancelar</Button>
          <Button 
            onClick={handleReject} 
            color="error" 
            variant="contained"
            disabled={!rejectionReason.trim() || actionLoading}
          >
            Rechazar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Documentos */}
      <Dialog open={openDocsDialog} onClose={() => setOpenDocsDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Documentos - Propuesta #{selectedProposal?.id}</DialogTitle>
        <DialogContent>
          {documents.length === 0 ? (
            <Typography>No hay documentos adjuntos</Typography>
          ) : (
            <Box>
              {documents.map((doc, index) => (
                <Box key={index} display="flex" alignItems="center" justifyContent="space-between" p={1} border={1} borderColor="divider" mb={1}>
                  <Typography>{doc.fileName || `Documento ${index + 1}`}</Typography>
                  <Button size="small" variant="outlined" onClick={() => handleDownloadDocument(doc)}>
                    Ver
                  </Button>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setOpenDocsDialog(false)}
            sx={{ '&:hover': { color: 'error.main', backgroundColor: 'transparent' } }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Backdrop de acciones (aprobar, rechazar, ver documentos) */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 2, flexDirection: 'column' }}
        open={actionLoading}
      >
        <CircularProgress color="inherit" thickness={5} />
        <Typography variant="body2" sx={{ mt: 2 }}>
          {actionMessage || 'Cargando...'}
        </Typography>
      </Backdrop>
    </Box>
  );
}


